/**
 * Onglet Fiches de paie - ÉTAPE G
 * Upload, navigation par année/mois
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Progress } from "../../ui/progress";
import { ScrollArea } from "../../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { 
  Upload, FileText, Plus, Eye, EyeOff, Download, Trash2, 
  X, Loader2, AlertCircle, Receipt, ChevronLeft, ChevronRight,
  Calendar, AlertTriangle, FolderUp, CheckCircle, XCircle, Clock,
  CheckSquare, RefreshCw
} from "lucide-react";
import { formatDateFr } from "../../ui/date-picker-fr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// French month names
const MONTH_NAMES = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

// Get month name
const getMonthName = (month) => MONTH_NAMES[month - 1] || "";

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// Max file size: 5 MB for payslips
const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Validate file
const validateFile = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: "Le fichier dépasse la taille maximale de 5 Mo" };
  }
  if (file.type !== "application/pdf" && !file.name.endsWith('.pdf')) {
    return { valid: false, error: "Seuls les fichiers PDF sont acceptés" };
  }
  return { valid: true };
};

// Payslip card component with salary reveal and selection
const PayslipCard = ({ payslip, onView, onDownload, onDelete, onRevealSalary, onReextract, selectionMode, isSelected, onToggleSelect }) => {
  const [salaryVisible, setSalaryVisible] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  
  // Auto-hide salary after 30 seconds
  useEffect(() => {
    let timer;
    if (salaryVisible) {
      timer = setTimeout(() => {
        setSalaryVisible(false);
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [salaryVisible]);
  
  const handleRevealSalary = () => {
    if (!salaryVisible) {
      // Log reveal to audit
      onRevealSalary(payslip);
    }
    setSalaryVisible(!salaryVisible);
  };
  
  const handleReextract = async () => {
    setReextracting(true);
    try {
      await onReextract(payslip);
    } finally {
      setReextracting(false);
    }
  };
  
  // Check if amounts are missing
  const hasMissingAmounts = !payslip.net_amount && !payslip.gross_amount;
  
  return (
    <div 
      className={`flex items-center gap-4 p-4 rounded-lg border transition-colors group ${
        isSelected 
          ? 'border-blue-500 bg-blue-50' 
          : hasMissingAmounts 
            ? 'border-amber-200 bg-amber-50/30'
            : 'border-slate-200 hover:bg-slate-50'
      }`}
      onClick={selectionMode ? () => onToggleSelect(payslip.id) : undefined}
    >
      {/* Checkbox for selection mode */}
      {selectionMode && (
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(payslip.id)}
            className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
        </div>
      )}
      
      {/* Month indicator */}
      <div className={`w-14 h-14 rounded-lg flex flex-col items-center justify-center text-white shrink-0 ${
        hasMissingAmounts 
          ? 'bg-gradient-to-br from-amber-400 to-amber-500' 
          : 'bg-gradient-to-br from-blue-500 to-blue-600'
      }`}>
        <span className="text-xs font-medium uppercase">{getMonthName(payslip.month).substring(0, 3)}</span>
        <span className="text-lg font-bold">{payslip.year}</span>
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">
          {getMonthName(payslip.month)} {payslip.year}
        </p>
        <p className="text-xs text-slate-500">
          {payslip.original_filename} • {formatFileSize(payslip.file_size)}
        </p>
        <p className="text-xs text-slate-400">
          Ajouté le {formatDateFr(payslip.uploaded_at)}
        </p>
        {hasMissingAmounts && (
          <p className="text-xs text-amber-600 font-medium mt-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Montants non extraits
          </p>
        )}
      </div>
      
      {/* Salary with reveal toggle - ALWAYS visible eye icon */}
      <div className="text-right flex items-center gap-2">
        {hasMissingAmounts ? (
          <Button
            variant="outline"
            size="sm"
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            onClick={handleReextract}
            disabled={reextracting}
          >
            {reextracting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1" />
            )}
            Réextraire
          </Button>
        ) : (
          <>
            <div>
              <p className="text-xs text-slate-500">Net à payer</p>
              <p className="text-sm font-semibold text-slate-900 font-mono">
                {salaryVisible && payslip.net_amount 
                  ? `${parseFloat(payslip.net_amount).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €` 
                  : "••••••"}
              </p>
              {salaryVisible && (
                <p className="text-xs text-slate-400">(masqué dans 30s)</p>
              )}
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7 text-slate-500 hover:text-blue-600"
                    onClick={handleRevealSalary}
                    data-testid={`toggle-salary-${payslip.id}`}
                    disabled={!payslip.net_amount}
                  >
                    {salaryVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{salaryVisible ? "Masquer" : "Afficher"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          onClick={() => onView(payslip)}
          title="Visualiser"
          data-testid={`view-payslip-${payslip.id}`}
        >
          <Eye className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          onClick={() => onDownload(payslip)}
          title="Télécharger"
          data-testid={`download-payslip-${payslip.id}`}
        >
          <Download className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => onDelete(payslip)}
          title="Supprimer"
          data-testid={`delete-payslip-${payslip.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Payslip viewer modal
const PayslipViewer = ({ payslip, open, onClose, token }) => {
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!open || !payslip) return;
    
    const fetchPayslip = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API}/hr/files/${payslip.file_id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        const url = URL.createObjectURL(response.data);
        setBlobUrl(url);
      } catch (err) {
        console.error("Error fetching payslip:", err);
        setError("Impossible de charger la fiche de paie");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayslip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payslip, token]);
  
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);
  
  const handleDownload = () => {
    if (blobUrl && payslip) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = payslip.original_filename;
      link.click();
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>Fiche de paie - {getMonthName(payslip?.month)} {payslip?.year}</span>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
          </DialogTitle>
          <DialogDescription>
            {payslip?.original_filename}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 overflow-auto" style={{ maxHeight: "70vh" }}>
          {loading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          )}
          
          {error && (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <AlertCircle className="w-12 h-12 mb-2" />
              <p>{error}</p>
            </div>
          )}
          
          {!loading && !error && blobUrl && payslip && (
            <iframe
              src={blobUrl}
              className="w-full h-[65vh] rounded-lg border"
              title={payslip.original_filename || "Fiche de paie"}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Empty state
const EmptyState = ({ onUpload, year }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <Receipt className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-slate-900 mb-2">
      {year ? `Aucune fiche de paie en ${year}` : "Aucune fiche de paie"}
    </h3>
    <p className="text-sm text-slate-500 mb-4 max-w-sm">
      Ajoutez les fiches de paie mensuelles pour ce salarié.
    </p>
    <Button onClick={onUpload} className="bg-blue-600 hover:bg-blue-700">
      <Plus className="w-4 h-4 mr-2" />
      Ajouter une fiche de paie
    </Button>
  </div>
);

// Year selector component with "All years" option
const YearSelector = ({ years, selectedYear, onSelect, onPrev, onNext, showAllYears = false }) => {
  // Sort years descending and get the max year from data
  const sortedYears = [...years].sort((a, b) => b - a);
  const maxYear = sortedYears.length > 0 ? sortedYears[0] : new Date().getFullYear();
  const minYear = sortedYears.length > 0 ? sortedYears[sortedYears.length - 1] : maxYear;
  
  const canGoNext = selectedYear !== "all" && selectedYear < maxYear;
  const canGoPrev = selectedYear !== "all" && selectedYear > minYear;
  
  return (
    <div className="flex items-center gap-2">
      <Button 
        variant="outline" 
        size="icon" 
        className="h-8 w-8"
        onClick={onPrev}
        disabled={!canGoPrev || selectedYear === "all"}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      
      <Select value={String(selectedYear)} onValueChange={(v) => onSelect(v === "all" ? "all" : Number(v))}>
        <SelectTrigger className="w-[150px] h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {showAllYears && (
            <SelectItem value="all">Toutes les années</SelectItem>
          )}
          {sortedYears.map(year => (
            <SelectItem key={year} value={String(year)}>{year}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      <Button 
        variant="outline" 
        size="icon" 
        className="h-8 w-8"
        onClick={onNext}
        disabled={!canGoNext || selectedYear === "all"}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default function PayslipsTab({ employee, onUpdate }) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  // Current year
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  // State
  const [payslips, setPayslips] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Upload state
  const [isUploadMode, setIsUploadMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ year: currentYear, month: currentMonth, gross_amount: "", net_amount: "" });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // OCR extraction state
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const [extractedFields, setExtractedFields] = useState({ gross: false, net: false });
  
  // Viewer state
  const [viewerPayslip, setViewerPayslip] = useState(null);
  
  // Delete state
  const [deletePayslip, setDeletePayslip] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Bulk import state
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const bulkInputRef = useRef(null);
  
  // Drag & drop state
  const [isDragging, setIsDragging] = useState(false);
  
  // Multi-select for bulk delete
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPayslips, setSelectedPayslips] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // All payslips (for "all years" view)
  const [allPayslips, setAllPayslips] = useState([]);
  
  // Fetch available years
  const fetchYears = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/hr/employees/${employee.id}/payslips/years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns array of {year, count} objects - extract just the years (sorted descending)
      const fetchedYears = (response.data.years || []).map(y => y.year).sort((a, b) => b - a);
      setYears(fetchedYears);
      
      // Set selected year to the most recent year with data
      if (fetchedYears.length > 0 && !fetchedYears.includes(selectedYear)) {
        setSelectedYear(fetchedYears[0]);
      }
    } catch (err) {
      console.error("Error fetching years:", err);
      // Default to current year if fetch fails
      setYears([currentYear]);
    }
  }, [employee.id, token, currentYear, selectedYear]);
  
  // Fetch payslips for selected year (or all years)
  const fetchPayslips = useCallback(async () => {
    setLoading(true);
    try {
      if (selectedYear === "all") {
        // Fetch all payslips for all years
        const response = await axios.get(
          `${API}/hr/employees/${employee.id}/payslips/all`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAllPayslips(response.data.payslips || []);
        setPayslips([]);
      } else {
        const response = await axios.get(
          `${API}/hr/employees/${employee.id}/payslips?year=${selectedYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPayslips(response.data.payslips || []);
        setAllPayslips([]);
      }
    } catch (err) {
      console.error("Error fetching payslips:", err);
      setError("Impossible de charger les fiches de paie");
    } finally {
      setLoading(false);
    }
  }, [employee.id, token, selectedYear]);
  
  useEffect(() => {
    fetchYears();
  }, [fetchYears]);
  
  useEffect(() => {
    fetchPayslips();
    // Clear selection when changing year
    setSelectedPayslips(new Set());
    setSelectionMode(false);
  }, [fetchPayslips]);
  
  // Get payslips for display (sorted by year desc, then month desc)
  const sortedPayslips = useMemo(() => {
    const source = selectedYear === "all" ? allPayslips : payslips;
    return [...source]
      .filter(p => !p.is_deleted)
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      });
  }, [payslips, allPayslips, selectedYear]);
  
  // Get months that already have payslips for the selected year
  const existingMonths = useMemo(() => {
    return payslips.filter(p => p.year === uploadForm.year && !p.is_deleted).map(p => p.month);
  }, [payslips, uploadForm.year]);
  
  // Log salary reveal to audit
  const handleRevealSalary = async (payslip) => {
    try {
      await axios.post(`${API}/hr/audit/reveal`, {
        employee_id: employee.id,
        field_name: "net_amount",
        resource_type: "payslip",
        resource_id: payslip.id,
        details: { month: payslip.month, year: payslip.year }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Error logging salary reveal:", err);
      // Don't block the UI if audit logging fails
    }
  };
  
  // Handle file selection with OCR extraction
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error("Fichier invalide", { description: validation.error });
      return;
    }
    
    setSelectedFile(file);
    setIsUploadMode(true);
    setUploadForm({ year: selectedYear, month: currentMonth, gross_amount: "", net_amount: "" });
    setExtractionError(null);
    setExtractedFields({ gross: false, net: false });
    
    // Trigger OCR extraction automatically
    setIsExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/hr/payslips/extract`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (response.data.success) {
        const extracted = { gross: false, net: false };
        const newForm = { year: selectedYear, month: currentMonth, gross_amount: "", net_amount: "" };
        const extractedAmounts = [];
        
        if (response.data.gross_amount !== null) {
          newForm.gross_amount = String(response.data.gross_amount);
          extracted.gross = true;
          extractedAmounts.push(`Brut: ${parseFloat(response.data.gross_amount).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €`);
        }
        
        if (response.data.net_amount !== null) {
          newForm.net_amount = String(response.data.net_amount);
          extracted.net = true;
          extractedAmounts.push(`Net: ${parseFloat(response.data.net_amount).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €`);
        }
        
        setUploadForm(prev => ({ ...prev, ...newForm }));
        setExtractedFields(extracted);
        
        if (extractedAmounts.length > 0) {
          toast.success("Extraction OCR réussie", {
            description: extractedAmounts.join(" • ")
          });
        } else {
          setExtractionError("Aucun montant trouvé");
          toast.warning("Extraction OCR", {
            description: "Les montants n'ont pas pu être extraits automatiquement"
          });
        }
      } else {
        setExtractionError(response.data.error || "Montants non trouvés");
        toast.warning("Extraction OCR", {
          description: response.data.error || "Les montants n'ont pas pu être extraits automatiquement"
        });
      }
    } catch (err) {
      console.error("OCR extraction error:", err);
      setExtractionError("Erreur lors de l'extraction");
      toast.warning("Extraction OCR indisponible", {
        description: "Vous pouvez saisir les montants manuellement"
      });
    } finally {
      setIsExtracting(false);
    }
  };
  
  // Cancel upload
  const cancelUpload = () => {
    setSelectedFile(null);
    setUploadForm({ year: currentYear, month: currentMonth, gross_amount: "", net_amount: "" });
    setIsUploadMode(false);
    setUploadProgress(0);
    setExtractionError(null);
    setExtractedFields({ gross: false, net: false });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  // Upload payslip
  const uploadPayslip = async () => {
    if (!selectedFile) return;
    
    // Check if month already has a payslip
    if (existingMonths.includes(uploadForm.month)) {
      toast.error("Fiche de paie existante", {
        description: `Une fiche de paie existe déjà pour ${getMonthName(uploadForm.month)} ${uploadForm.year}`
      });
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("year", String(uploadForm.year));
      formData.append("month", String(uploadForm.month));
      
      // Add gross_amount if provided
      if (uploadForm.gross_amount) {
        formData.append("gross_amount", uploadForm.gross_amount);
      }
      
      // Add net_amount if provided
      if (uploadForm.net_amount) {
        formData.append("net_amount", uploadForm.net_amount);
      }
      
      await axios.post(
        `${API}/hr/employees/${employee.id}/payslips`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );
      
      toast.success("Fiche de paie ajoutée avec succès !", {
        description: `${getMonthName(uploadForm.month)} ${uploadForm.year}`,
        duration: 4000
      });
      
      // Refresh data
      if (uploadForm.year !== selectedYear) {
        setSelectedYear(uploadForm.year);
      } else {
        await fetchPayslips();
      }
      await fetchYears();
      onUpdate?.();
      cancelUpload();
      
    } catch (err) {
      console.error("Error uploading payslip:", err);
      toast.error("Erreur", { 
        description: err.response?.data?.detail || "Impossible d'ajouter la fiche de paie" 
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  // View payslip
  const handleView = (payslip) => {
    setViewerPayslip(payslip);
  };
  
  // Download payslip
  const handleDownload = async (payslip) => {
    try {
      const response = await axios.get(
        `${API}/hr/files/${payslip.file_id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = payslip.original_filename;
      link.click();
      URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error("Error downloading payslip:", err);
      toast.error("Erreur", { description: "Impossible de télécharger la fiche de paie" });
    }
  };
  
  // Delete click
  const handleDeleteClick = (payslip) => {
    setDeletePayslip(payslip);
  };
  
  // Confirm delete
  const confirmDelete = async () => {
    if (!deletePayslip) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/hr/payslips/${deletePayslip.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Fiche de paie supprimée", {
        description: `${getMonthName(deletePayslip.month)} ${deletePayslip.year}`,
        action: {
          label: "Annuler",
          onClick: () => handleRestore(deletePayslip)
        },
        duration: 8000
      });
      
      setDeletePayslip(null);
      await fetchPayslips();
      await fetchYears();
      onUpdate?.();
      
    } catch (err) {
      console.error("Error deleting payslip:", err);
      toast.error("Erreur", { description: "Impossible de supprimer la fiche de paie" });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Restore payslip
  const handleRestore = async (payslip) => {
    try {
      await axios.post(`${API}/hr/payslips/${payslip.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Fiche de paie restaurée");
      await fetchPayslips();
      await fetchYears();
      onUpdate?.();
      
    } catch (err) {
      console.error("Error restoring payslip:", err);
      toast.error("Erreur", { description: "Impossible de restaurer la fiche de paie" });
    }
  };
  
  // Re-extract amounts for a payslip
  const handleReextract = async (payslip) => {
    try {
      const response = await axios.post(
        `${API}/hr/payslips/${payslip.id}/reextract`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success("Montants extraits avec succès", {
          description: `Net: ${response.data.extracted?.net_amount?.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €`
        });
        await fetchPayslips();
      } else {
        toast.error("Extraction échouée", {
          description: response.data.errors?.join(", ") || "Impossible d'extraire les montants"
        });
      }
    } catch (err) {
      console.error("Error re-extracting payslip:", err);
      toast.error("Erreur", { 
        description: err.response?.data?.detail || "Impossible de réextraire les montants" 
      });
    }
  };
  
  // Selection handlers for bulk delete
  const toggleSelection = (payslipId) => {
    setSelectedPayslips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(payslipId)) {
        newSet.delete(payslipId);
      } else {
        newSet.add(payslipId);
      }
      return newSet;
    });
  };
  
  const selectAll = () => {
    setSelectedPayslips(new Set(sortedPayslips.map(p => p.id)));
  };
  
  const deselectAll = () => {
    setSelectedPayslips(new Set());
  };
  
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedPayslips(new Set());
    }
  };
  
  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedPayslips.size === 0) return;
    
    if (!confirm(`Supprimer ${selectedPayslips.size} fiche(s) de paie ? Elles seront déplacées dans la corbeille.`)) {
      return;
    }
    
    setBulkDeleting(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const payslipId of selectedPayslips) {
      try {
        await axios.delete(`${API}/hr/payslips/${payslipId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        successCount++;
      } catch (err) {
        console.error(`Error deleting payslip ${payslipId}:`, err);
        errorCount++;
      }
    }
    
    setBulkDeleting(false);
    setSelectedPayslips(new Set());
    setSelectionMode(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} fiche(s) supprimée(s)`);
      await fetchPayslips();
      await fetchYears();
      onUpdate?.();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} fiche(s) n'ont pas pu être supprimées`);
    }
  };
  
  // Bulk import handlers
  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    
    if (validFiles.length !== files.length) {
      toast.warning("Fichiers ignorés", {
        description: `${files.length - validFiles.length} fichier(s) non-PDF ont été ignorés`
      });
    }
    
    setBulkFiles(validFiles);
    setBulkResults(null);
  };

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    const validFiles = files.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    
    if (validFiles.length === 0) {
      toast.error("Fichiers non valides", {
        description: "Seuls les fichiers PDF sont acceptés"
      });
      return;
    }
    
    if (validFiles.length !== files.length) {
      toast.warning("Fichiers ignorés", {
        description: `${files.length - validFiles.length} fichier(s) non-PDF ont été ignorés`
      });
    }
    
    setBulkImportOpen(true);
    setBulkFiles(validFiles);
    setBulkResults(null);
  };
  
  const handleBulkImport = async () => {
    if (bulkFiles.length === 0) return;
    
    setBulkImporting(true);
    setBulkResults(null);
    
    // Show start message - estimate ~10 seconds per file
    const estimatedTime = Math.ceil(bulkFiles.length * 10 / 60);
    const toastId = toast.loading(`Import en cours de ${bulkFiles.length} fichier(s)...`, {
      description: `Estimation: ~${estimatedTime > 1 ? estimatedTime + ' minutes' : '1 minute'} (extraction IA en cours)`,
      duration: Infinity
    });
    
    try {
      const formData = new FormData();
      bulkFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await axios.post(
        `${API}/hr/employees/${employee.id}/payslips/bulk-import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 600000 // 10 minutes timeout for bulk imports (10 sec per file * 60 files max)
        }
      );
      
      // Dismiss loading toast
      toast.dismiss(toastId);
      
      setBulkResults(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Import terminé avec succès !`, {
          description: `${response.data.success} fiche(s) importée(s) sur ${response.data.total}`,
          duration: 5000
        });
        await fetchPayslips();
        await fetchYears();
        onUpdate?.();
      } else if (response.data.skipped > 0) {
        toast.warning("Import terminé", {
          description: `${response.data.skipped} fiche(s) ignorée(s) (déjà existantes)`,
          duration: 5000
        });
      } else {
        toast.error("Import terminé", {
          description: "Aucune fiche n'a pu être importée. Vérifiez les détails ci-dessous.",
          duration: 5000
        });
      }
      
    } catch (err) {
      toast.dismiss(toastId);
      console.error("Bulk import error:", err);
      
      // Check if it's a timeout error
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.warning("Import en cours...", {
          description: "La requête a pris trop de temps, mais l'import peut continuer côté serveur. Rafraîchissez la page dans quelques instants.",
          duration: 10000
        });
      } else {
        toast.error("Erreur d'import", {
          description: err.response?.data?.detail || "Une erreur est survenue lors de l'import",
          duration: 5000
        });
      }
    } finally {
      setBulkImporting(false);
    }
  };
  
  const closeBulkImport = () => {
    setBulkImportOpen(false);
    setBulkFiles([]);
    setBulkResults(null);
    if (bulkInputRef.current) bulkInputRef.current.value = '';
  };
  
  // Year navigation
  const handlePrevYear = () => {
    const sortedYears = [...years].sort((a, b) => b - a);
    const currentIndex = sortedYears.indexOf(selectedYear);
    if (currentIndex < sortedYears.length - 1) {
      setSelectedYear(sortedYears[currentIndex + 1]);
    }
  };
  
  const handleNextYear = () => {
    if (selectedYear < currentYear) {
      setSelectedYear(selectedYear + 1);
    }
  };
  
  // Render loading
  if (loading && payslips.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  // Render error
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertCircle className="w-12 h-12 mb-2" />
        <p>{error}</p>
        <Button variant="outline" onClick={fetchPayslips} className="mt-4">
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div 
      className="space-y-6 relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-blue-500/10 border-2 border-dashed border-blue-500 rounded-lg flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white p-6 rounded-xl shadow-lg text-center">
            <Upload className="w-12 h-12 text-blue-500 mx-auto mb-3" />
            <p className="text-lg font-semibold text-slate-800">Déposez vos fichiers PDF ici</p>
            <p className="text-sm text-slate-500 mt-1">Les fiches de paie seront importées automatiquement</p>
          </div>
        </div>
      )}
      
      {/* Header with year selector and upload button */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <YearSelector
          years={years}
          selectedYear={selectedYear}
          onSelect={setSelectedYear}
          onPrev={handlePrevYear}
          onNext={handleNextYear}
          showAllYears={true}
        />
        
        <div className="flex gap-2 flex-wrap">
          {/* Selection mode toggle */}
          {sortedPayslips.length > 0 && (
            <Button 
              variant={selectionMode ? "secondary" : "outline"}
              onClick={toggleSelectionMode}
              className="gap-2"
            >
              {selectionMode ? (
                <>
                  <X className="w-4 h-4" />
                  Annuler
                </>
              ) : (
                <>
                  <CheckSquare className="w-4 h-4" />
                  Sélectionner
                </>
              )}
            </Button>
          )}
          
          {/* Bulk delete button */}
          {selectionMode && selectedPayslips.size > 0 && (
            <Button 
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="gap-2"
            >
              {bulkDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Supprimer ({selectedPayslips.size})
            </Button>
          )}
          
          {/* Select all / deselect all */}
          {selectionMode && (
            <Button 
              variant="outline"
              onClick={selectedPayslips.size === sortedPayslips.length ? deselectAll : selectAll}
              className="gap-2"
            >
              {selectedPayslips.size === sortedPayslips.length ? "Tout désélectionner" : "Tout sélectionner"}
            </Button>
          )}
          
          <input
            ref={bulkInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            onChange={handleBulkFileSelect}
            className="hidden"
          />
          {!selectionMode && (
            <Button 
              onClick={() => {
                setBulkImportOpen(true);
                setTimeout(() => bulkInputRef.current?.click(), 100);
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="bulk-import-payslip-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une fiche de paie
            </Button>
          )}
        </div>
      </div>
      
      {/* Drag & drop hint */}
      <div className="flex items-center justify-center gap-2 py-2 px-4 bg-slate-50 rounded-lg border border-dashed border-slate-200 text-slate-500 text-sm">
        <Upload className="w-4 h-4" />
        <span>Glissez-déposez vos fichiers PDF ici ou utilisez le bouton ci-dessus</span>
      </div>
      
      {/* Upload form */}
      {isUploadMode && selectedFile && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-600" />
              Nouvelle fiche de paie
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File info */}
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <FileText className="w-8 h-8 text-red-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{selectedFile.name}</p>
                <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
              </div>
              {!isUploading && (
                <Button variant="ghost" size="icon" onClick={cancelUpload}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            {/* Progress */}
            {isUploading && (
              <div className="space-y-1">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-xs text-slate-500">{uploadProgress}%</p>
              </div>
            )}
            
            {/* Form fields */}
            {!isUploading && (
              <div className="space-y-4">
                {/* OCR Extraction Status */}
                {isExtracting && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Extraction OCR en cours...</p>
                      <p className="text-xs text-blue-600">Analyse du document pour détecter le montant net à payer</p>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Year */}
                  <div className="space-y-1.5">
                    <Label className="text-sm">Année *</Label>
                    <Select 
                      value={String(uploadForm.year)} 
                      onValueChange={(v) => setUploadForm(prev => ({ ...prev, year: Number(v) }))}
                      disabled={isExtracting}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[currentYear, currentYear - 1, currentYear - 2].map(year => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Month */}
                  <div className="space-y-1.5">
                    <Label className="text-sm">Mois *</Label>
                    <Select 
                      value={String(uploadForm.month)} 
                      onValueChange={(v) => setUploadForm(prev => ({ ...prev, month: Number(v) }))}
                      disabled={isExtracting}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTH_NAMES.map((name, index) => {
                          const month = index + 1;
                          const isExisting = existingMonths.includes(month) && uploadForm.year === selectedYear;
                          return (
                            <SelectItem 
                              key={month} 
                              value={String(month)}
                              disabled={isExisting}
                            >
                              {name} {isExisting && "(déjà ajouté)"}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Amounts - extracted via OCR or manual entry */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Gross Amount */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Salaire brut (€)</Label>
                      {extractedFields.gross && uploadForm.gross_amount && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                          <Eye className="w-3 h-3 mr-1" />
                          OCR
                        </Badge>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={uploadForm.gross_amount}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, gross_amount: e.target.value }))}
                        placeholder={isExtracting ? "Extraction..." : "Ex: 3500.00"}
                        disabled={isExtracting}
                        className="w-full h-10 px-3 py-2 text-sm border rounded-md border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
                        data-testid="payslip-gross-amount-input"
                      />
                      {isExtracting && (
                        <Loader2 className="absolute right-3 top-2.5 w-5 h-5 animate-spin text-blue-500" />
                      )}
                    </div>
                  </div>
                  
                  {/* Net Amount */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Net à payer (€)</Label>
                      {extractedFields.net && uploadForm.net_amount && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                          <Eye className="w-3 h-3 mr-1" />
                          OCR
                        </Badge>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={uploadForm.net_amount}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, net_amount: e.target.value }))}
                        placeholder={isExtracting ? "Extraction..." : "Ex: 2145.87"}
                        disabled={isExtracting}
                        className="w-full h-10 px-3 py-2 text-sm border rounded-md border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
                        data-testid="payslip-net-amount-input"
                      />
                      {isExtracting && (
                        <Loader2 className="absolute right-3 top-2.5 w-5 h-5 animate-spin text-blue-500" />
                      )}
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-slate-500">
                  {extractionError 
                    ? "L'extraction automatique n'a pas fonctionné. Veuillez saisir les montants manuellement."
                    : "Ces montants seront chiffrés et affichés uniquement sur demande."
                  }
                </p>
              </div>
            )}
            
            {/* Actions */}
            {!isUploading && (
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={cancelUpload} disabled={isExtracting}>
                  Annuler
                </Button>
                <Button 
                  onClick={uploadPayslip} 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={isExtracting}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Payslips list */}
      {!isUploadMode && (
        <>
          {sortedPayslips.length === 0 ? (
            <EmptyState onUpload={() => fileInputRef.current?.click()} year={selectedYear} />
          ) : (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-blue-600" />
                  {selectedYear === "all" 
                    ? `Toutes les fiches de paie (${sortedPayslips.length})` 
                    : `Fiches de paie ${selectedYear} (${sortedPayslips.length})`}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="max-h-[calc(100vh-400px)] min-h-[300px] overflow-y-auto pr-2 space-y-3">
                  {sortedPayslips.map(payslip => (
                    <PayslipCard
                      key={payslip.id}
                      payslip={payslip}
                      onView={handleView}
                      onDownload={handleDownload}
                      onDelete={handleDeleteClick}
                      onRevealSalary={handleRevealSalary}
                      onReextract={handleReextract}
                      selectionMode={selectionMode}
                      isSelected={selectedPayslips.has(payslip.id)}
                      onToggleSelect={toggleSelection}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Payslip viewer */}
      <PayslipViewer
        payslip={viewerPayslip}
        open={!!viewerPayslip}
        onClose={() => setViewerPayslip(null)}
        token={token}
      />
      
      {/* Delete confirmation */}
      <AlertDialog open={!!deletePayslip} onOpenChange={() => setDeletePayslip(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette fiche de paie ?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                La fiche de paie de <strong>{getMonthName(deletePayslip?.month)} {deletePayslip?.year}</strong> sera 
                déplacée dans la corbeille.
              </p>
              <p className="text-amber-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Elle sera définitivement supprimée dans 30 jours.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Bulk Import Modal */}
      <Dialog open={bulkImportOpen} onOpenChange={closeBulkImport}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderUp className="w-5 h-5 text-blue-600" />
              Import en masse des fiches de paie
            </DialogTitle>
            <DialogDescription>
              Sélectionnez plusieurs fichiers PDF. Le système détectera automatiquement la période et les montants.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File selection area */}
            {bulkFiles.length === 0 && !bulkResults && (
              <div 
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer"
                onClick={() => bulkInputRef.current?.click()}
              >
                <FolderUp className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                <p className="text-sm font-medium text-slate-700">Cliquez pour sélectionner des fichiers PDF</p>
                <p className="text-xs text-slate-500 mt-1">ou glissez-déposez les fichiers ici</p>
              </div>
            )}
            
            {/* Selected files list */}
            {bulkFiles.length > 0 && !bulkResults && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">
                    {bulkFiles.length} fichier(s) sélectionné(s)
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setBulkFiles([]);
                      if (bulkInputRef.current) bulkInputRef.current.value = '';
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Effacer
                  </Button>
                </div>
                
                <ScrollArea className="max-h-[250px]">
                  <div className="space-y-2">
                    {bulkFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <FileText className="w-5 h-5 text-red-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 truncate">{file.name}</p>
                          <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Extraction automatique :</strong> Le système analysera chaque PDF pour détecter :
                  </p>
                  <ul className="text-xs text-blue-700 mt-1 ml-4 list-disc">
                    <li>La période (mois et année)</li>
                    <li>Le salaire brut</li>
                    <li>Le salaire net à payer</li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Import progress */}
            {bulkImporting && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                <p className="text-sm font-medium text-slate-700">Import en cours...</p>
                <p className="text-xs text-slate-500">Analyse et extraction des données</p>
              </div>
            )}
            
            {/* Results */}
            {bulkResults && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-700">{bulkResults.success}</p>
                    <p className="text-xs text-green-600">Importé(s)</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                    <Clock className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-amber-700">{bulkResults.skipped}</p>
                    <p className="text-xs text-amber-600">Ignoré(s)</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <XCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-red-700">{bulkResults.failed}</p>
                    <p className="text-xs text-red-600">Échec(s)</p>
                  </div>
                </div>
                
                {/* Details */}
                <ScrollArea className="max-h-[250px]">
                  <div className="space-y-2">
                    {bulkResults.details?.map((detail, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-start gap-3 p-2 rounded-lg ${
                          detail.status === 'success' ? 'bg-green-50' :
                          detail.status === 'skipped' ? 'bg-amber-50' : 'bg-red-50'
                        }`}
                      >
                        {detail.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                        ) : detail.status === 'skipped' ? (
                          <Clock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{detail.filename}</p>
                          <p className={`text-xs ${
                            detail.status === 'success' ? 'text-green-600' :
                            detail.status === 'skipped' ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {detail.message}
                          </p>
                          {detail.extracted_data && detail.status === 'success' && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              {detail.extracted_data.gross_amount && `Brut: ${parseFloat(detail.extracted_data.gross_amount).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}€`}
                              {detail.extracted_data.gross_amount && detail.extracted_data.net_amount && ' • '}
                              {detail.extracted_data.net_amount && `Net: ${parseFloat(detail.extracted_data.net_amount).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}€`}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={closeBulkImport}>
              {bulkResults ? 'Fermer' : 'Annuler'}
            </Button>
            {bulkFiles.length > 0 && !bulkResults && (
              <Button 
                onClick={handleBulkImport}
                disabled={bulkImporting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {bulkImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Import...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importer {bulkFiles.length} fichier(s)
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
